
// pages/customers/CustomerForm.jsx

import { useEffect, useState } from "react";
import { customersAPI, getCmId } from "../../services/api";
import { useRouter } from "../../contexts/RouterContext";
import { useToast } from "../../contexts/ToastContext";
import {
  Breadcrumb,
  Loading,
  Spinner,
} from "../../components/ui";

export default function CustomerForm({ editId }) {
  const { navigate } = useRouter();
  const toast = useToast();

  const isEdit = !!editId;

  const [form, setForm] = useState({
    name: "",
    phone: "",
    address: "",
    reference: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [apiError, setApiError] = useState("");

  // --------------------------------------------------
  // HANDLE FORM INPUTS
  // --------------------------------------------------

  const setF = (key) => (e) => {
    setForm((f) => ({
      ...f,
      [key]: e.target.value,
    }));

    if (errors[key]) {
      setErrors((prev) => ({
        ...prev,
        [key]: "",
      }));
    }

    setApiError("");
  };

  // --------------------------------------------------
  // FETCH CUSTOMER DETAILS FOR EDIT
  // --------------------------------------------------

  useEffect(() => {
    if (!isEdit) return;

    const fetchCustomer = async () => {
      try {
        const c = await customersAPI.get(editId);

        if (c?.customer) {
          setForm({
            name: c.customer.name || "",
            phone: c.customer.phone || "",
            address: c.customer.address || "",
            reference: c.customer.reference || "",
          });
        }
      } catch (error) {
        console.error(
          "[CustomerForm] Fetch error:",
          error
        );

        setApiError(
          `❌ Error loading customer: ${error.message}`
        );
      } finally {
        setFetching(false);
      }
    };

    fetchCustomer();
  }, [editId, isEdit]);

  // --------------------------------------------------
  // VALIDATION
  // --------------------------------------------------

  const validate = () => {
    const e = {};

    if (!form.name.trim()) {
      e.name = "Customer name is required";
    }

    if (!form.phone.trim()) {
      e.phone = "Phone number is required";
    } else if (
      !/^\d{10}$/.test(form.phone.replace(/\s/g, ""))
    ) {
      e.phone = "Enter a valid 10-digit phone number";
    }

    return e;
  };

  // --------------------------------------------------
  // SUBMIT FORM
  // --------------------------------------------------

  
  const handleSubmit = async () => {
    const e = validate();

    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }

    const cm_id = getCmId();

    if (!cm_id) {
      setApiError(
        "❌ Company not linked. Please check your company_master configuration and log in again."
      );
      return;
    }

    setLoading(true);
    setApiError("");

    try {
      // ----------------------------------------------
      // EDIT CUSTOMER
      // ----------------------------------------------

      if (isEdit) {
        const updated = await customersAPI.update(editId, form);

        if (
          updated?.status &&
          updated.status !== "success"
        ) {
          throw new Error(
            updated.message || "Customer update failed."
          );
        }

        toast("Customer updated successfully!");

        // Use the existing customer ID
        navigate(
          "customer-detail",
          { id: editId },
          { replace: true }
        );
      }

      // ----------------------------------------------
      // ADD NEW CUSTOMER
      // ----------------------------------------------

      else {
        const created = await customersAPI.create(form);

        console.log(
          "[CustomerForm] Create Response:",
          created
        );

        if (
          created?.status !== "success" ||
          !created?.LM_ID
        ) {
          throw new Error(
            created?.message ||
            "Customer creation failed. Customer ID not received."
          );
        }

        const newCustomerId = created.LM_ID;

        console.log(
          "[CustomerForm] New Customer ID:",
          newCustomerId
        );

        toast("Customer added successfully!");

        // IMPORTANT: Replace Add Customer with Customer Details
        navigate(
          "customer-detail",
          { id: newCustomerId },
          { replace: true }
        );
      }

    } catch (err) {
      console.error(
        "[CustomerForm] Submit error:",
        err
      );

      setApiError(`❌ Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };


  // --------------------------------------------------
  // LOADING
  // --------------------------------------------------

  if (fetching) {
    return (
      <div className="page">
        <Loading />
      </div>
    );
  }

  // --------------------------------------------------
  // PAGE UI
  // --------------------------------------------------

  return (
    <div className="page">

      <Breadcrumb
        items={[
          {
            label: "Customers",
            onClick: () => navigate("customers"),
          },
          {
            label: isEdit
              ? "Edit Customer"
              : "Add Customer",
          },
        ]}
      />

      <div className="page-header">
        <div>
          <h1 className="page-title">
            {isEdit
              ? "Edit Customer"
              : "Add New Customer"}
          </h1>

          <p className="page-sub">
            {isEdit
              ? "Update the customer's details below"
              : "Fill in the details to register a new customer"}
          </p>
        </div>
      </div>

      <div
        className="card"
        style={{ maxWidth: 680 }}
      >

        <div
          className="form-section-header"
          style={{ marginBottom: 18 }}
        >
          <span>👤 Customer Information</span>
        </div>

        {/* API ERROR BANNER */}

        {apiError && (
          <div
            style={{
              background: "var(--red-dim)",
              border: "1px solid rgba(224,85,85,0.3)",
              borderRadius: "var(--radius-sm)",
              padding: "12px 14px",
              marginBottom: 16,
              fontSize: 13,
              color: "var(--red)",
              lineHeight: 1.6,
            }}
          >
            {apiError}
          </div>
        )}

        <div className="form-grid">

          {/* CUSTOMER NAME */}

          <div className="form-group">
            <label className="form-label">
              Customer Name{" "}
              <span className="form-required">*</span>
            </label>

            <input
              className={`form-input${
                errors.name ? " error" : ""
              }`}
              value={form.name}
              onChange={setF("name")}
              placeholder="e.g. Arjun Mehta"
            />

            {errors.name && (
              <span className="form-error">
                {errors.name}
              </span>
            )}
          </div>

          {/* PHONE NUMBER */}

          <div className="form-group">
            <label className="form-label">
              Phone Number{" "}
              <span className="form-required">*</span>
            </label>

            <input
              className={`form-input${
                errors.phone ? " error" : ""
              }`}
              value={form.phone}
              onChange={setF("phone")}
              placeholder="10-digit mobile number"
              maxLength={10}
            />

            {errors.phone && (
              <span className="form-error">
                {errors.phone}
              </span>
            )}
          </div>

          {/* ADDRESS */}

          <div className="form-group form-full">
            <label className="form-label">
              Address
            </label>

            <input
              className="form-input"
              value={form.address}
              onChange={setF("address")}
              placeholder="Full address with city"
            />
          </div>

          {/* REFERENCE */}

          <div className="form-group form-full">
            <label className="form-label">
              Reference
            </label>

            <input
              className="form-input"
              value={form.reference}
              onChange={setF("reference")}
              placeholder="Enter Reference Name"
            />
          </div>

        </div>

        {/* ACTION BUTTONS */}

        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            marginTop: 8,
          }}
        >

          <button
            className="btn btn-ghost"
            onClick={() =>
              navigate(
                isEdit
                  ? "customer-detail"
                  : "customers",
                isEdit
                  ? { id: editId }
                  : {}
              )
            }
            disabled={loading}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary btn-lg"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner /> Saving…
              </>
            ) : isEdit ? (
              "Save Changes"
            ) : (
              "Add Customer →"
            )}
          </button>

        </div>

      </div>
    </div>
  );
}
